import { useCallback } from 'react'
import * as Sentry from '@sentry/react'
import { useAuthContext } from '../context/auth'
import { MindmapService } from '../services/mindmap/mindmapService'
import { useCachedLoader } from './useCachedLoader'
import type { MindMapMetadata, MindMapEndpoints } from '@bandos/shared'

/**
 * Data hook for mindmap CRUD operations.
 * Provides access to the user's mindmaps list and operations to create, update, delete.
 *
 * This is a data-layer hook (not UI-specific) so it lives in hooks/
 * and can be used by any component that needs mindmap data.
 */
export function useMindmaps() {
  const { user } = useAuthContext()

  // Use cached loader for mindmaps list
  const {
    data: mindmaps,
    loading,
    reload: loadMindmaps,
    setData: setMindmaps,
  } = useCachedLoader<MindMapMetadata[]>(user?.id ?? null, async () => MindmapService.list(), {
    initialValue: [],
  })

  const createMindmap = useCallback(
    async (mindmapData: MindMapEndpoints['create']['body']) => {
      try {
        const result = await MindmapService.create(mindmapData)

        if (result.data) {
          const newMindmap = result.data
          setMindmaps((prev) => [newMindmap, ...prev])
        }

        return result
      } catch (error) {
        console.error('[useMindmaps] Error creating mindmap:', error)
        Sentry.captureException(error, {
          tags: { feature: 'mindmap', action: 'create_mindmap' },
        })
        return { data: null, error: { message: 'Failed to create mindmap' } }
      }
    },
    [setMindmaps]
  )

  const updateMindmap = useCallback(
    async (id: string, updates: MindMapEndpoints['update']['body']) => {
      try {
        const result = await MindmapService.update(id, updates)

        if (result.data) {
          const updatedData = result.data
          setMindmaps((prev) =>
            prev.map((mindmap) => (mindmap.id === id ? { ...mindmap, ...updatedData } : mindmap))
          )
        }

        return result
      } catch (error) {
        console.error('[useMindmaps] Error updating mindmap:', error)
        Sentry.captureException(error, {
          tags: { feature: 'mindmap', action: 'update_mindmap' },
        })
        return { data: null, error: { message: 'Failed to update mindmap' } }
      }
    },
    [setMindmaps]
  )

  const duplicateMindmap = useCallback(
    async (id: string) => {
      try {
        const result = await MindmapService.duplicate(id)

        if (result.data) {
          const newMindmap = result.data
          setMindmaps((prev) => [newMindmap, ...prev])
        }

        return result
      } catch (error) {
        console.error('[useMindmaps] Error duplicating mindmap:', error)
        Sentry.captureException(error, {
          tags: { feature: 'mindmap', action: 'duplicate_mindmap' },
        })
        return { data: null, error: { message: 'Failed to duplicate mindmap' } }
      }
    },
    [setMindmaps]
  )

  const deleteMindmap = useCallback(
    async (id: string) => {
      try {
        const result = await MindmapService.delete(id)

        if (!result.error) {
          setMindmaps((prev) => prev.filter((mindmap) => mindmap.id !== id))
        }

        return result
      } catch (error) {
        console.error('[useMindmaps] Error deleting mindmap:', error)
        Sentry.captureException(error, {
          tags: { feature: 'mindmap', action: 'delete_mindmap' },
        })
        return { data: null, error: { message: 'Failed to delete mindmap' } }
      }
    },
    [setMindmaps]
  )

  return {
    mindmaps,
    loading,
    loadMindmaps,
    createMindmap,
    updateMindmap,
    duplicateMindmap,
    deleteMindmap,
  }
}
